import React, { useState } from "react";
import { Box, Button, ButtonBase, CircularProgress, Typography } from "@mui/material";
import { Check, DescriptionOutlined, ErrorOutline, KeyboardArrowDown, PriorityHigh, ReportProblemOutlined } from "@mui/icons-material";
import { colors } from "../../theme/designSystem";
import type { StatementReview, StatementReviewLine } from "../../services/assistantsService";

// Cartão do extrato na área Livro-caixa (item 228, F3a — RN-571). O selo diz se dá para confiar na leitura; sem
// saldos que conferem, nada é mostrado como comparado. Molde no canvas "Assistentes · Livro-caixa" (quadros 1 e 4,
// aprovado pelo Hilton em 28/10).

export type StatementCardState =
  | { kind: "loading"; fileName: string; accountTitle: string }
  | { kind: "refused"; fileName: string; message: string }
  | { kind: "review"; fileName: string; review: StatementReview; recognizedBy?: string | null };

// RN-574: como a conta foi definida. "numero" e "memoria" = reconhecida; sem valor = a pessoa escolheu
const HOW_ACCOUNT: Record<string, string> = {
  numero: "reconhecida pelo número da conta no extrato",
  memoria: "reconhecida pela escolha anterior da equipe",
};

const AccountLine: React.FC<{ title: string; recognizedBy?: string | null; onChange?: () => void }> = ({ title, recognizedBy, onChange }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 8px", px: 2, py: 1, fontSize: "0.8125rem", color: colors.textSecondary, borderTop: `1px solid ${colors.backgroundSubtle}` }}>
    <span>Conta: <b style={{ color: colors.textStrong }}>{title}</b> · {recognizedBy ? HOW_ACCOUNT[recognizedBy] ?? "reconhecida" : "escolhida por você"}</span>
    {onChange && (
      <ButtonBase onClick={onChange} sx={{ minHeight: 32, px: 0.5, fontFamily: "inherit", fontSize: "0.8125rem", fontWeight: 600, color: colors.primaryText, textDecoration: "underline", textUnderlineOffset: "3px" }}>
        Trocar conta
      </ButtonBase>
    )}
  </Box>
);

const SUCCESS_TEXT = "#1e6b31";
const ERROR_TEXT = "#a71d2a";
const SUCCESS_BORDER = "#b7dfc2";
const ERROR_BORDER = "#f1b0b7";
const WARNING_BORDER = "#ffe08a";
const WARNING_FILL = "#fff8e6";

const money = (value: number) => value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signed = (value: number) => `${value < 0 ? "−" : "+"} ${money(Math.abs(value))}`;
const brl = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : `${value < 0 ? "− " : ""}R$ ${money(Math.abs(value))}`;
const day = (iso: string | null | undefined, withYear = false) => {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return withYear ? `${d}/${m}/${y}` : `${d}/${m}`;
};

const Badge: React.FC<{ fg: string; bg: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ fg, bg, icon, children }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0, fontSize: "0.75rem", fontWeight: 600, color: fg, bgcolor: bg,
    px: 1.25, py: 0.375, borderRadius: "10px", whiteSpace: "nowrap", "& svg": { fontSize: 13 } }}>
    {icon}{children}
  </Box>
);

const Header: React.FC<{ icon: React.ReactNode; tone: { fg: string; bg: string }; title: string; meta: string; badge?: React.ReactNode }> = ({ icon, tone, title, meta, badge }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.5, borderBottom: `1px solid ${colors.backgroundSubtle}` }}>
    <Box aria-hidden sx={{ width: 32, height: 32, borderRadius: "8px", flexShrink: 0, bgcolor: tone.bg, color: tone.fg, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 } }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: colors.textStrong, overflowWrap: "anywhere" }}>{title}</Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: colors.textSecondary, overflowWrap: "anywhere" }}>{meta}</Typography>
    </Box>
    {badge}
  </Box>
);

/** Lista que mostra as primeiras linhas e abre o resto num toque; no celular cada linha quebra em duas. */
const LineList: React.FC<{
  title: string;
  hint?: string;
  rows: { key: string; date: string; text: string; amount: number }[];
  isMobile: boolean;
  defaultOpen?: boolean;
}> = ({ title, hint, rows, isMobile, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [all, setAll] = useState(false);
  if (rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, 5);
  return (
    <Box sx={{ borderTop: `1px solid ${colors.backgroundSubtle}` }}>
      <ButtonBase onClick={() => setOpen((v) => !v)} aria-expanded={open}
        sx={{ width: "100%", minHeight: 48, px: 2, justifyContent: "space-between", fontFamily: "inherit", fontSize: "0.875rem", fontWeight: 600, color: colors.textStrong,
          "&:hover": { bgcolor: colors.background }, "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: -2 } }}>
        {title}
        <KeyboardArrowDown aria-hidden sx={{ color: colors.textSecondary, transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease-out" }} />
      </ButtonBase>
      {open && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          {hint && <Typography sx={{ fontSize: "0.8125rem", color: colors.textPrimary, mb: 1 }}>{hint}</Typography>}
          <Box role="list" sx={{ display: "flex", flexDirection: "column" }}>
            {shown.map((r) => (
              <Box key={r.key} role="listitem"
                sx={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr) auto" : "48px minmax(0, 1fr) 112px", columnGap: 1.5, rowGap: 0.25,
                  py: 0.75, borderBottom: `1px solid ${colors.backgroundSubtle}`, fontSize: "0.875rem", fontVariantNumeric: "tabular-nums" }}>
                {!isMobile && <Box component="span" sx={{ color: colors.textSecondary }}>{day(r.date)}</Box>}
                <Box component="span" sx={{ minWidth: 0, color: colors.textStrong, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap" }}>
                  {isMobile && <Box component="span" sx={{ color: colors.textSecondary, mr: 1 }}>{day(r.date)}</Box>}
                  {r.text}
                </Box>
                <Box component="span" sx={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap", color: r.amount < 0 ? ERROR_TEXT : SUCCESS_TEXT }}>{signed(r.amount)}</Box>
              </Box>
            ))}
          </Box>
          {rows.length > 5 && !all && (
            <Button onClick={() => setAll(true)} sx={{ mt: 1, px: 0.5, minHeight: 36, textTransform: "none", fontWeight: 600, color: colors.primaryText }}>
              Ver os {rows.length}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

const Shell: React.FC<{ border: string; raised?: boolean; children: React.ReactNode; label: string }> = ({ border, raised, children, label }) => (
  <Box component="section" aria-label={label}
    sx={{ border: `1px solid ${border}`, borderRadius: "8px", bgcolor: colors.white, overflow: "hidden", maxWidth: 640,
      boxShadow: raised ? "0 2px 10px rgba(33, 37, 41, 0.08)" : "none" }}>
    {children}
  </Box>
);

const Comparison: React.FC<{ review: StatementReview; isMobile: boolean }> = ({ review, isMobile }) => {
  const fresh = review.lines.filter((l: StatementReviewLine) => l.status === "new");
  const counter = (value: number, label: string, tone: "plain" | "new" | "warn") => (
    <Box sx={{ px: isMobile ? 1 : 2, py: 1.5, textAlign: isMobile ? "center" : "left",
      bgcolor: tone === "new" ? colors.primaryLight : tone === "warn" && value > 0 ? WARNING_FILL : colors.white }}>
      <Typography sx={{ fontSize: isMobile ? "1.375rem" : "1.625rem", fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: "tabular-nums",
        color: tone === "new" ? colors.primaryText : tone === "warn" && value > 0 ? colors.warningSoftText : colors.textStrong }}>{value}</Typography>
      <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.8125rem", color: colors.textPrimary }}>{label}</Typography>
    </Box>
  );
  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", "& > *:not(:last-of-type)": { borderRight: `1px solid ${colors.backgroundSubtle}` } }}>
        {counter(review.inBookCount, isMobile ? "no livro" : "já estão no livro", "plain")}
        {counter(review.newCount, isMobile ? "novos" : "novos, ainda não lançados", "new")}
        {counter(review.onlyInBook.length, isMobile ? "sem par" : "no livro, sem par no extrato", "warn")}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: 1.25, px: 2, py: 1.5,
        borderTop: `1px solid ${colors.backgroundSubtle}`, fontVariantNumeric: "tabular-nums" }}>
        {[
          { label: "Saldo inicial", value: brl(review.openingBalance), color: colors.textStrong },
          { label: "Entradas", value: `+ R$ ${money(review.credits)}`, color: SUCCESS_TEXT },
          { label: "Saídas", value: `− R$ ${money(Math.abs(review.debits))}`, color: ERROR_TEXT },
          { label: "Saldo final", value: brl(review.closingBalance), color: colors.textStrong },
        ].map((b) => (
          <Box key={b.label}>
            <Typography sx={{ fontSize: "0.8125rem", color: colors.textSecondary }}>{b.label}</Typography>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: b.color }}>{b.value}</Typography>
          </Box>
        ))}
      </Box>
      <LineList title={`Novos (${fresh.length})`} isMobile={isMobile} defaultOpen={!isMobile}
        rows={fresh.map((l) => ({ key: `n${l.row}`, date: l.date, text: l.description, amount: l.amount }))} />
      <LineList title={`No livro, sem par no extrato (${review.onlyInBook.length})`} isMobile={isMobile}
        hint="Lançamentos do período que não aparecem no extrato. Costuma ser valor digitado diferente: confira no Livro-caixa."
        rows={review.onlyInBook.map((b, i) => ({ key: `b${i}`, date: b.date, text: b.description, amount: b.amount }))} />
    </>
  );
};

const Footnote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ px: 2, py: 1.25, fontSize: "0.75rem", color: colors.textSecondary, bgcolor: colors.background, borderTop: `1px solid ${colors.backgroundSubtle}` }}>
    {children}
  </Typography>
);

const StatementCard: React.FC<{ state: StatementCardState; isMobile: boolean; onChangeAccount?: () => void }> = ({ state, isMobile, onChangeAccount }) => {
  // Arquivo sem saldo: os totais precisam do olho de quem tem o extrato do banco antes de qualquer comparação
  const [totalsOk, setTotalsOk] = useState<boolean | null>(null);

  if (state.kind === "loading") {
    return (
      <Shell border={colors.border} label="Lendo o extrato">
        <Header icon={<DescriptionOutlined />} tone={{ fg: colors.primaryText, bg: colors.primaryLight }} title={state.fileName} meta={state.accountTitle} />
        <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.75, fontSize: "0.875rem", color: colors.textPrimary }}>
          <CircularProgress size={16} aria-hidden /> Lendo e conferindo o saldo linha a linha…
        </Box>
      </Shell>
    );
  }

  if (state.kind === "refused") {
    return (
      <Shell border={colors.borderLight} label="Arquivo não lido">
        <Header icon={<ErrorOutline />} tone={{ fg: colors.textPrimary, bg: colors.backgroundAlt }} title={state.fileName} meta="Não foi lido" />
        <Typography sx={{ px: 2, py: 1.5, fontSize: "0.875rem", color: colors.textPrimary, lineHeight: 1.5 }}>{state.message}</Typography>
      </Shell>
    );
  }

  const r = state.review;
  const account = <AccountLine title={r.accountTitle} recognizedBy={state.recognizedBy} onChange={onChangeAccount} />;
  const period = r.periodStart && r.periodEnd ? `${day(r.periodStart)} a ${day(r.periodEnd, true)}` : "";
  const meta = [period, `${r.lines.length} movimentos`].filter(Boolean).join(" · ");
  const warnings = r.warnings.length > 0 && (
    <Box sx={{ px: 2, py: 1.25, display: "flex", flexDirection: "column", gap: 0.5, borderTop: `1px solid ${colors.backgroundSubtle}` }}>
      {r.warnings.map((w) => (
        <Typography key={w} sx={{ fontSize: "0.8125rem", color: colors.warningSoftText, display: "flex", gap: 0.75, alignItems: "flex-start" }}>
          <ReportProblemOutlined aria-hidden sx={{ fontSize: 16, mt: "2px" }} />{w}
        </Typography>
      ))}
    </Box>
  );

  if (r.proof === "BROKEN") {
    return (
      <Shell border={ERROR_BORDER} label="Extrato não conferido">
        <Header icon={<PriorityHigh />} tone={{ fg: ERROR_TEXT, bg: colors.errorSoft }} title="Não consegui conferir" meta={`${state.fileName} · ${r.accountTitle}`}
          badge={<Badge fg={ERROR_TEXT} bg={colors.errorSoft}>Saldo não fecha</Badge>} />
        {account}
        <Typography sx={{ px: 2, py: 1.5, fontSize: "0.875rem", color: colors.textPrimary, lineHeight: 1.5 }}>
          {r.brokenAtRow
            ? `Na linha ${r.brokenAtRow} do arquivo o saldo não confere com os movimentos anteriores.`
            : "O saldo final do arquivo não confere com a soma dos movimentos."}{" "}
          Nada foi comparado com o livro. Envie o mesmo extrato em outro formato (CSV ou Excel costumam funcionar).
        </Typography>
        {warnings}
      </Shell>
    );
  }

  if (r.proof === "NONE" && totalsOk !== true) {
    return (
      <Shell border={WARNING_BORDER} raised={totalsOk === null} label="Conferir os totais do extrato">
        <Header icon={<DescriptionOutlined />} tone={{ fg: colors.warningSoftText, bg: colors.warningSoft }} title="Confira os totais" meta={`${meta} · ${r.accountTitle}`}
          badge={<Badge fg={colors.warningSoftText} bg={colors.warningSoft}>Sem saldo no arquivo</Badge>} />
        {account}
        <Typography sx={{ px: 2, py: 1.5, fontSize: "0.875rem", color: colors.textPrimary, lineHeight: 1.5, fontVariantNumeric: "tabular-nums" }}>
          {totalsOk === false
            ? "Então o arquivo não está completo. Exporte o extrato de novo no internet banking, de preferência em CSV ou Excel com a coluna de saldo."
            : <>Entradas <b style={{ color: SUCCESS_TEXT }}>+ R$ {money(r.credits)}</b> · Saídas <b style={{ color: ERROR_TEXT }}>− R$ {money(Math.abs(r.debits))}</b>. Batem com o extrato do banco?</>}
        </Typography>
        {totalsOk === null && (
          <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 1, px: 2, py: 1.5, bgcolor: colors.background, borderTop: `1px solid ${colors.backgroundSubtle}` }}>
            <Button variant="contained" onClick={() => setTotalsOk(true)} sx={{ minHeight: isMobile ? 44 : 40, px: 2.5, fontWeight: 600, textTransform: "none", boxShadow: "none" }}>Batem, seguir</Button>
            <Button variant="outlined" onClick={() => setTotalsOk(false)} sx={{ minHeight: isMobile ? 44 : 40, px: 2, textTransform: "none", color: colors.textPrimary, borderColor: colors.border }}>Não batem</Button>
          </Box>
        )}
        {warnings}
      </Shell>
    );
  }

  const nothingNew = r.newCount === 0 && r.onlyInBook.length === 0;
  return (
    <Shell border={nothingNew ? SUCCESS_BORDER : colors.border} raised={!nothingNew} label="Resumo do extrato">
      <Header icon={nothingNew ? <Check /> : <DescriptionOutlined />}
        tone={nothingNew ? { fg: SUCCESS_TEXT, bg: colors.successSoft } : { fg: colors.primaryText, bg: colors.primaryLight }}
        title={nothingNew ? "Nada a lançar" : `Extrato conferido · ${r.accountTitle}`}
        meta={nothingNew ? `${meta} · todos já estão no livro` : meta}
        badge={<Badge fg={SUCCESS_TEXT} bg={colors.successSoft} icon={<Check />}>{r.proof === "CLOSED" ? "Saldos conferem" : "Totais conferidos"}</Badge>} />
      {account}
      {!nothingNew && <Comparison review={r} isMobile={isMobile} />}
      {warnings}
      <Footnote>Nada foi lançado. O arquivo foi lido e descartado.</Footnote>
    </Shell>
  );
};

export default StatementCard;
